import {
  Component,
  Input,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'
import { ProductComponent } from '../../../../types/chemistry'
import { MatDividerModule } from '@angular/material/divider'
import { MatIconModule } from '@angular/material/icon'
import { NgOptimizedImage } from '@angular/common'
import { DialogService } from '../../../../services/dialog.service'
import { Dialog } from '@angular/cdk/dialog'
import { TranslationService } from '../../../../services/translation.service'
import { Router, RouterModule } from '@angular/router'
import { MatBottomSheet } from '@angular/material/bottom-sheet'
import { SubstancesOfConcernComponent } from '../../substances-of-concern/substances-of-concern.component'
import { MatTooltip } from '@angular/material/tooltip'
import { firstValueFrom } from 'rxjs'
import { DocumentService } from '../../../../services/document.service'
import { ApiError } from '../../../../types/apiError'
import { ErrorService } from '../../../../services/error.service'
import { SpinnerService } from '../../../../services/spinner.service'

@Component({
  selector: 'app-chemistry-info',
  imports: [
    MatIconModule,
    MatDividerModule,
    NgOptimizedImage,
    RouterModule,
    MatTooltip,
  ],
  templateUrl: './chemistry-info.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './chemistry-info.component.scss',
})
export class ChemistryInfoComponent {
  @Input() chemistry?: ProductComponent
  @Input() isProduct = false
  @Input() isMobileView = false

  dialog = inject(Dialog)
  dialogService = inject(DialogService)
  translationService = inject(TranslationService)
  documentService = inject(DocumentService)
  errorService = inject(ErrorService)
  spinnerService = inject(SpinnerService)
  router = inject(Router)
  private _bottomSheet = inject(MatBottomSheet)

  /**
   * Returns file path for a GHS classification image
   * @param {string} ghs Name of GHS classification
   * @returns {string} The file path
   */
  getGhsImgPath(ghsCode: string): string {
    return `assets/ghs/${ghsCode.toLowerCase()}.svg`
  }

  /**
   * Formats 'producedInCanada' value
   * @returns {string} The formatted value
   */
  getProducedInCanadaString(): string {
    if (this.chemistry?.productionInCanada === true) {
      return this.translationService.getYesText()
    } else if (this.chemistry?.productionInCanada === false) {
      return this.translationService.getNoText()
    } else {
      return this.translationService.getUnwillingToDiscloseText()
    }
  }

  /**
   * Determines the display value for a chemistry category.
   *
   * @param {string | null} categoryName - The name of the chemistry category.
   * @returns {string} The display value for the category. Returns "Other" text if the category name is null or empty, otherwise returns the category name.
   */
  getCategoryDisplayValue(categoryName: string | null): string {
    return categoryName == null || categoryName.trim().length === 0
      ? this.translationService.getOtherText()
      : categoryName
  }

  /**
   * Opens dialog to show a product's safe use instructions
   */
  showSafeUseInstructions() {
    if (!this.chemistry) {
      return
    }

    this.dialogService.showDialog(
      this.dialog,
      this.translationService.getSafeUseInstructionsText(),
      this.chemistry.safeUseInstructions,
    )
  }

  /**
   * Opens the Product Ingredients screen given a product id in a new tab
   * @param {string} productId Id of the product
   */
  openProductIngredients(productId: string) {
    const url = this.router
      .createUrlTree(['/products', productId, 'ingredients'])
      .toString()
    window.open(url, '_blank')
  }

  /**
   * Opens a document given its base64 string
   * @param documentId The id for the document
   */
  downloadAndShowDocument(documentId: string) {
    if (!documentId) {
      return
    }

    this.spinnerService.startSpinner()

    firstValueFrom(this.documentService.get(documentId))
      .then(({ document }: { document: string }) => {
        if (!document) {
          throw Error('An error occurred retrieving the document')
        }

        const fileBlob: Blob = this.base64ToBlob(document)
        window.open(URL.createObjectURL(fileBlob), '_blank')
      })
      .catch((err) => {
        const apiErr = err as ApiError
        this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
      })
      .finally(() => this.spinnerService.stopSpinner())
  }

  /**
   * Converts document from base 64 to blob
   * @param base64 Document in base 64
   * @returns
   */
  base64ToBlob(base64: string) {
    const byteCharacters = atob(base64)
    const byteNumbers = new Array(byteCharacters.length)
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i)
    }
    const byteArray = new Uint8Array(byteNumbers)
    return new Blob([byteArray], { type: 'application/pdf' })
  }

  /**
   * Opens bottom sheet to show substances of concern
   */
  showSubstancesOfConcern() {
    this._bottomSheet.open(SubstancesOfConcernComponent, {
      data: {
        mandatorySubstancesOfConcern:
          this.chemistry?.mandatorySubstancesOfConcern || [],
        voluntarySubstancesOfConcern:
          this.chemistry?.voluntarySubstancesOfConcern || [],
      },
    })
  }
}
